import {
  Button,
  Modal,
  Label,
  InputText,
  ErrorMessage,
  InputScore,
  Checkbox,
  TEOLoader,
} from '@teo/components';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useGet } from '@/query';
import TextWithTooltip from '@/pages/Protected/Manage/Competencies/TextWithTooltip';
import {
  TrashIcon,
  ArrowLeft,
  CrossFilled,
  Plus,
  Search,
} from '@teo/components/icons';
import { PaginationAnything } from '@/components/DataTable/PaginationAnything';
import { config } from '@/config';
import { axios } from '@/axios';
import { newUid } from '@/util';
import { SelectSearchDropdown } from '../SelectSearchDropdown';

const NodeItem = ({ comp, isMandatory, handleMandatory, handeleDelete }) => {
  return (
    <div
      className={`flex flex-row items-center rounded-md border px-2 py-1 ${
        isMandatory && 'bg-secondary-01'
      }`}
    >
      <p className="min-w-[80px] text-xs text-grey-06">{comp?.code}</p>
      <div className="flex flex-row items-center text-sm text-grey-08">
        {comp?.competenceCategoryColorCode && (
          <span
            style={{
              backgroundColor: comp?.competenceCategoryColorCode,
            }}
            className={`mr-2 h-2 w-2 min-w-2 rounded-full`}
          ></span>
        )}
        {comp.name}
      </div>
      <div className="ml-auto flex w-[140px] flex-row items-center justify-end">
        <div>
          <Checkbox
            checked={isMandatory}
            onChange={(isCheck) => handleMandatory(comp, isCheck)}
          />
        </div>
        <div>
          <Button
            variant="ghost"
            className="ml-5 !px-2"
            onClick={() => handeleDelete(comp)}
          >
            <TrashIcon className="h-4 w-4 text-grey-08" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddProfileModal = ({
  closeModal,
  setIsOpenAddModal,
  setRefetchTable,
  authUser,
  edit = false,
  setActiveProfile,
  activeProfile,
  categories,
}) => {
  const { t } = useTranslation();
  const [checkValid, setCheckValid] = useState(false);
  const [nameProfile, setNameProfile] = useState(activeProfile?.name || '');
  const [minScore, setMinScore] = useState(
    activeProfile?.minScore ? Math.round(activeProfile?.minScore * 100) : 0
  );
  const [mandatory, setMandatory] = useState([]);

  const [editProfileComp, setEditProfileComp] = useState([]);

  const [loadCompetencies, setLoadCompetencies] = useState(true);
  const [sort, setSort] = useState([{ id: 'competenceId', desc: false }]);
  const [mandatoryListEdit, setMandatoryListEdit] = useState([]);
  const [deleteListEdit, setDeleteListEdit] = useState([]);

  const [openAddCompetencies, setOpenAddCompetencies] = useState(false);
  const [competenciesProfile, setCompetenciesProfile] = useState([]);
  const [category, setCategory] = useState({
    id: 0,
    name: t('pages.manage_competencies.all_categories'),
  });
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [addList, setAddList] = useState([]);
  const [competenceListEmpty, setCompetenceListEmpty] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);

  const [loade, setLoade] = useState(edit ? false : true);

  useGet(`/professioncompetencedetails`, {
    enabled: edit,
    professionId: activeProfile?.id,
    onSuccess: (result) => {
      setEditProfileComp(result?.data || []);
      const numMandatory = result?.data?.filter((el) => el?.isMandatory);
      setMandatory(numMandatory);
      if (result?.data?.length > 0) {
        setCompetenceListEmpty(false);
      } else {
        setCompetenceListEmpty(true);
      }
      setLoade(true);
    },
  });

  const handleSaveProfile = async () => {
    setCheckValid(true);

    if ((!nameProfile || minScore === 0) && openAddCompetencies) {
      setOpenAddCompetencies(false);
    }

    if (!nameProfile || minScore === 0) return;

    try {
      if (edit) {
        const changes = {};

        if (nameProfile !== activeProfile?.name) {
          changes.name = nameProfile;
        }

        if (minScore !== Math.round(activeProfile?.minScore * 100)) {
          changes.minScore = minScore / 100;
        }

        await axios.put(`${config.backend}/profession/${activeProfile?.id}`, {
          ...changes,
        });

        if (mandatoryListEdit?.length > 0) {
          await Promise.all(
            mandatoryListEdit.map((el) =>
              axios.put(`${config.backend}/professioncompetence/${el.id}`, {
                isMandatory: el?.isMandatory,
              })
            )
          );
        }

        if (deleteListEdit?.length > 0) {
          await Promise.all(
            deleteListEdit.map((el) =>
              axios.delete(`${config.backend}/professioncompetence/${el.id}`)
            )
          );
        }

        if (addList?.length > 0) {
          await Promise.all(
            addList.map((el) =>
              axios.post(`${config.backend}/professioncompetences`, {
                professionId: activeProfile.id,
                competenceId: el?.competenceId,
                isMandatory: el?.isMandatory,
              })
            )
          );
        }

        setRefetchTable(newUid(20));
        setActiveProfile(null);
        document.body.style.overflow = 'unset';
        setIsOpenAddModal(false);
      } else {
        const newProfile = await axios.post(`${config.backend}/professions`, {
          organisationId: authUser?.activeOrganisationId,
          name: nameProfile,
          minScore: minScore / 100,
          lang: authUser?.lang,
        });

        const profileId = newProfile?.data?.id;

        if (!profileId) return;

        if (addList?.length > 0) {
          await Promise.all(
            addList.map((el) =>
              axios.post(`${config.backend}/professioncompetences`, {
                professionId: profileId,
                competenceId: el?.competenceId,
                isMandatory: el?.isMandatory,
              })
            )
          );
        }

        setRefetchTable(newUid(20));
        document.body.style.overflow = 'unset';
        setIsOpenAddModal(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleMandatory = (comp, isCheck) => {
    if (!competenceListEmpty) {
      setMandatoryListEdit((prev) => {
        const exists = prev.some((item) => item.id === comp.id);

        if (exists) {
          return prev.filter((item) => item.id !== comp.id);
        }

        return [...prev, { ...comp, isMandatory: isCheck }];
      });

      const exists = mandatory?.some((el) => el.id === comp.id);
      if (exists && !isCheck) {
        setMandatory((prev) =>
          prev?.filter((el) => el?.competenceId !== comp.competenceId)
        );
      }
      if (!exists && isCheck) {
        setMandatory((prev) => [...prev, { ...comp, isMandatory: isCheck }]);
      }
    } else {
      const editList = addList?.map((el) =>
        el?.competenceId === comp?.competenceId
          ? { ...el, isMandatory: isCheck }
          : el
      );
      setAddList(editList);
    }
  };

  const handeleDelete = (comp) => {
    if (!competenceListEmpty) {
      setDeleteListEdit((prev) => [...prev, comp]);
      const exists = mandatoryListEdit?.find((el) => el?.id === comp.id);
      if (exists) {
        const newMandatoryList = mandatoryListEdit.filter(
          (item) => item.id !== comp.id
        );
        setMandatoryListEdit(newMandatoryList);
      }
      setMandatory((prev) =>
        prev?.filter((el) => el?.competenceId !== comp.competenceId)
      );
    } else {
      setAddList((prev) =>
        prev?.filter((el) => el?.competenceId !== comp.competenceId)
      );
    }
  };

  const handleError = (el) => (el ? false : checkValid ? true : false);

  const hendleAdd = (item) => {
    setAddList((prev) => [...prev, item]);
  };
  const hendleDelete = (item) => {
    setAddList((prev) =>
      prev?.filter((el) => el?.competenceId !== item.competenceId)
    );
  };

  useEffect(() => {
    setMandatory(addList?.filter((el) => el?.isMandatory));
  }, [addList]);

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-xl  rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            <h2 className="text-sm font-semibold">
              {edit
                ? t('pages.manage_competencies.profile_modal.edit_profile')
                : t('pages.manage_competencies.profile_modal.new_profile')}
            </h2>
          </Modal.Header>

          <Modal.Body>
            <div
              className={`-ml-6 -mr-6 -mt-6 flex max-h-[65vh] flex-col overflow-y-auto border-b ${
                openAddCompetencies ? 'p-0' : 'p-6'
              }`}
            >
              {!openAddCompetencies ? (
                <div className="flex flex-col gap-5">
                  <div>
                    <h3 className="text-lg">
                      {edit
                        ? activeProfile?.name
                        : t(
                            'pages.manage_competencies.profile_modal.add_profile'
                          )}
                    </h3>
                  </div>

                  <div className="flex w-full flex-col">
                    <div className="mb-1 text-sm text-black">
                      <Label htmlFor="competenceName">
                        {t('pages.manage_competencies.tab_categories.name')}
                      </Label>
                      <InputText
                        id="competenceName"
                        onChange={(e) => setNameProfile(e.currentTarget.value)}
                        value={nameProfile}
                        hasError={handleError(nameProfile)}
                      />
                      {handleError(nameProfile) && (
                        <ErrorMessage id="errorMessage">
                          {t('pages.onboarding.error.text_fiels')}
                        </ErrorMessage>
                      )}
                    </div>

                    <div className=" mb-1 mt-2 w-full text-sm text-black">
                      <div className="mb-2 flex w-full items-center">
                        <p className="text-sm-semibold">
                          {t(
                            'pages.manage_competencies.profile_modal.performance_condition'
                          )}
                        </p>
                      </div>
                      <div>
                        <InputScore
                          decreaseButtonLabel="Decrease Score"
                          increaseButtonLabel="Increase Score"
                          value={minScore}
                          minScore={0}
                          onChange={(value) => setMinScore(value)}
                          suffix="%"
                          step={5}
                          hasError={handleError(minScore)}
                        />
                        {handleError(minScore) && (
                          <ErrorMessage id="errorMessage">
                            {t('pages.onboarding.error.text_fiels')}
                          </ErrorMessage>
                        )}
                        <div>
                          <p
                            className="pt-2 text-grey-08"
                            dangerouslySetInnerHTML={{
                              __html: t(
                                'pages.manage_competencies.profile_modal.performance_text',
                                {
                                  achievedCompetencies: minScore,
                                  mandatory: mandatory?.length,
                                }
                              ),
                            }}
                          ></p>
                        </div>
                      </div>
                    </div>

                    {editProfileComp?.length > 0 ||
                    (competenceListEmpty && addList?.length > 0) ? (
                      <div
                        className={`${
                          (competenceListEmpty ||
                            editProfileComp?.length !=
                              deleteListEdit?.length) &&
                          'mt-4 rounded-md border p-2'
                        }`}
                      >
                        <div className="flex w-full flex-col">
                          {(competenceListEmpty ||
                            editProfileComp?.length !=
                              deleteListEdit?.length) && (
                            <div className="rowm flex">
                              <p className="text-sm-medium w-[110px] px-1 pb-1 text-left text-grey-06">
                                {t('columns.code')}
                              </p>
                              <p className="text-sm-medium px-1 pb-1 text-left text-grey-06">
                                {t('columns.competence')}
                              </p>
                              <p className="text-sm-medium ml-auto w-[140px] px-1 pb-1 text-left text-grey-06">
                                {t('pages.create_courses.mandatory')}
                              </p>
                            </div>
                          )}
                          {!competenceListEmpty ? (
                            <div className="flex w-full flex-col">
                              {loadCompetencies && (
                                <div className="mx-auto flex justify-center py-20 px-10">
                                  <TEOLoader
                                    loaderSrc="/images/TEOloader.gif"
                                    className="w-[100px]"
                                  />
                                </div>
                              )}
                              <PaginationAnything
                                className={`flex w-full flex-col gap-2 ${
                                  loadCompetencies === true && ' hidden '
                                }`}
                                fetch={useGet.bind(
                                  null,
                                  '/professioncompetencedetails',
                                  {
                                    professionId: activeProfile.id,
                                    onSuccess: (result) => {
                                      setLoadCompetencies(false);
                                    },
                                  }
                                )}
                                empty={{
                                  template: 2,
                                  loadContent: loadCompetencies,
                                  text: t('empty_tenplate.course_list'),
                                }}
                                defaultSort={sort}
                                page={0}
                                pageSize={10}
                                render={(comp) => {
                                  const exists = mandatoryListEdit?.find(
                                    (el) => el?.id === comp.id
                                  );

                                  const isMandatory = exists
                                    ? exists?.isMandatory
                                    : comp?.isMandatory;

                                  const existsComp = deleteListEdit?.find(
                                    (el) => el?.id === comp.id
                                  );

                                  if (existsComp) return null;
                                  return (
                                    <NodeItem
                                      key={comp?.competenceId}
                                      comp={comp}
                                      isMandatory={isMandatory}
                                      handleMandatory={handleMandatory}
                                      handeleDelete={handeleDelete}
                                    />
                                  );
                                }}
                              />
                            </div>
                          ) : (
                            <div className="flex w-full flex-col gap-2">
                              {addList?.map((comp) => {
                                const isMandatory = comp?.isMandatory;
                                return (
                                  <NodeItem
                                    key={comp?.competenceId}
                                    comp={comp}
                                    isMandatory={isMandatory}
                                    handleMandatory={handleMandatory}
                                    handeleDelete={handeleDelete}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`flex flex-col items-center px-4 pt-8 ${
                            !loade && ' hidden '
                          }`}
                        >
                          <div className="mb-8">
                            <img
                              className="m-auto w-36"
                              src="/images/components/notifications/theo-empty-inbox.svg"
                            />
                          </div>
                          <div className="mb-4 max-w-[320px] text-center">
                            <p className="text-base font-semibold text-black">
                              {t(
                                'pages.manage_competencies.profile_modal.profile_empty'
                              )}
                            </p>
                          </div>
                          <div>
                            <Button
                              variant="outline"
                              onClick={() => setOpenAddCompetencies(true)}
                            >
                              {t('pages.manage_competencies.add_competence')}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="px-6 pt-6">
                    <Button
                      variant="text"
                      onClick={() => setOpenAddCompetencies(false)}
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="px-6">
                      <h3 className="mb-3 text-lg">
                        {t('pages.manage_competencies.add_competence')}
                      </h3>
                    </div>

                    <div className={`relative mb-2 px-6`}>
                      <InputText
                        value={filter}
                        onChange={(ev) => setFilter(ev.target.value)}
                        Icon={Search}
                        placeholder={t('search.course')}
                      />
                    </div>
                    <div className="z-[10] max-w-[250px] px-6">
                      <SelectSearchDropdown
                        clearButtonLabel="clear"
                        onChange={async (cat) => {
                          setCategory(cat);
                        }}
                        buttonText={`${t(
                          'pages.create_edit_course.select_language'
                        )}`}
                        options={[{ id: 0, name: 'All' }, ...categories]}
                        searchable={true}
                        value={category?.name ? category : undefined}
                      >
                        {(option) => (
                          <span className="flex items-center">
                            {option.color && (
                              <span
                                style={{ backgroundColor: option.color }}
                                className={`mr-2 h-2 w-2 rounded-full`}
                              ></span>
                            )}
                            {option.name}
                          </span>
                        )}
                      </SelectSearchDropdown>
                    </div>

                    <div className="flex max-h-[35vh] w-full flex-col overflow-y-auto px-6">
                      {loadCompetencies && (
                        <div className="mx-auto flex justify-center py-20 px-10">
                          <TEOLoader
                            loaderSrc="/images/TEOloader.gif"
                            className="w-[100px]"
                          />
                        </div>
                      )}
                      <PaginationAnything
                        className={`my-3 flex w-full flex-col gap-2 ${
                          loadCompetencies === true && ' hidden '
                        }`}
                        fetch={useGet.bind(
                          null,
                          '/organisationcompetencedetails',
                          {
                            lang: authUser?.lang,
                            organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
                            disabled: false,
                            onSuccess: (result) => {
                              setLoadCompetencies(false);
                            },
                          }
                        )}
                        empty={{
                          template: 2,
                          loadContent: loadCompetencies,
                          text: t('empty_tenplate.course_list'),
                        }}
                        defaultSort={sort}
                        page={0}
                        pageSize={10}
                        filter={debouncedFilter}
                        render={(comp) => {
                          const newComp = addList?.find(
                            (el) => el.competenceId === comp.competenceId
                          );
                          return (
                            <div
                              key={comp?.competenceId}
                              className={`group flex w-full cursor-pointer flex-col gap-6 rounded-lg border sm:flex-row ${
                                newComp && 'bg-grey-01'
                              }`}
                            >
                              <div className="flex w-full items-center px-3 py-2 text-black">
                                {comp?.competenceCategoryColorCode && (
                                  <span
                                    style={{
                                      backgroundColor:
                                        comp?.competenceCategoryColorCode,
                                    }}
                                    className={`mr-2 h-1.5 w-1.5 min-w-1.5 rounded-full`}
                                  ></span>
                                )}
                                {comp.name}

                                <div className="ml-auto">
                                  <p className="mx-3 min-w-max text-xs text-grey-06">
                                    {comp?.code}
                                  </p>
                                </div>
                                <div>
                                  {newComp ? (
                                    <Button
                                      className="!px-2"
                                      variant="ghost"
                                      onClick={() => {
                                        hendleDelete(comp);
                                      }}
                                    >
                                      <CrossFilled className="h-4 w-4 text-grey-06" />
                                    </Button>
                                  ) : (
                                    <Button
                                      className="!px-2"
                                      variant="ghost"
                                      onClick={() => hendleAdd(comp)}
                                    >
                                      <Plus className="h-4 w-4 text-grey-06" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 flex">
              <Button
                variant="ghost"
                onClick={() => {
                  document.body.style.overflow = 'unset';
                  setIsOpenAddModal(false);
                }}
                className="mr-auto"
              >
                {t('button.cancel')}
              </Button>
              {competenceListEmpty &&
                addList?.length > 0 &&
                !openAddCompetencies && (
                  <Button
                    variant="outline"
                    onClick={() => setOpenAddCompetencies(true)}
                    className="mr-2"
                  >
                    {t('pages.manage_competencies.add_competence')}
                  </Button>
                )}
              <Button onClick={() => handleSaveProfile()}>
                {edit ? t('button.edit') : t('button.add_btn')}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </div>
    </div>
  );
};

export default AddProfileModal;
